import BoltIcon from '@/assets/bolt.svg?react';
import CloseIcon from '@/assets/close-line.svg?react';
import highVoltage from '@/assets/img/transmission-lines/high-voltage.png';
import lowVoltage from '@/assets/img/transmission-lines/low-voltage.png';
import mediumVoltage from '@/assets/img/transmission-lines/medium-voltage.png';
import transformerPole from '@/assets/img/transmission-lines/transformer-pole.png';
import transmissionLine from '@/assets/img/transmission-lines/transmission-line.png';
import QuestionCircleIcon from '@/assets/question-circle.svg?react';
import RightArrowIcon from '@/assets/right-arrow.svg?react';
import Button from '@/components/ui/Button';
import Modal, {
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalTitle,
  ModalTrigger,
} from '@/components/ui/Modal';

import TransmissionLineTypeItem from './TransmissionLineTypeItem';
import { useLingui } from '@lingui/react';

interface TransmissionLineTypesModalProps {
  variant?: 'default' | 'full';
}

const TransmissionLineTypesModal = ({
  variant = 'default',
}: TransmissionLineTypesModalProps) => {
  const { i18n } = useLingui();

  const lineTypes = [
    {
      photo: lowVoltage,
      badgeLabel: i18n.t('do 1 kV'),
      badgeClassName: 'bg-[rgba(46,184,82,0.13)] text-[#2eb852]',
      title: i18n.t('Niskie napięcie'),
      description: i18n.t(
        'Najczęściej zasila domy i małe budynki. Zwykle są to niższe słupy, często z przyłączami do posesji.'
      ),
    },
    {
      photo: mediumVoltage,
      badgeLabel: i18n.t('1–60 kV'),
      badgeClassName: 'bg-[rgba(140,199,20,0.13)] text-[#8cc714]',
      title: i18n.t('Średnie napięcie'),
      description: i18n.t(
        'Linia doprowadza prąd do lokalnych stacji transformatorowych. Często ma 3 przewody i większe izolatory.'
      ),
    },
    {
      photo: highVoltage,
      badgeLabel: i18n.t('110 kV i więcej'),
      badgeClassName: 'bg-[rgba(255,153,13,0.13)] text-[#ff990d]',
      title: i18n.t('Wysokie napięcie'),
      description: i18n.t(
        'Duże linie na wysokich słupach lub kratownicach. Przenoszą energię na większe odległości.'
      ),
    },
    {
      photo: transmissionLine,
      badgeLabel: i18n.t('Różne napięcia'),
      badgeClassName: 'bg-[rgba(255,71,20,0.13)] text-[#ff4714]',
      title: i18n.t('Linia przesyłowa'),
      description: i18n.t(
        'Przewody nad działką lub pod ziemią. Warto wskazać ten typ, jeśli widać samą linię.'
      ),
    },
    {
      photo: transformerPole,
      badgeLabel: i18n.t('SN/nN'),
      badgeClassName: 'bg-[rgba(158,20,224,0.13)] text-[#9e14e0]',
      title: i18n.t('Słup z transformatorem'),
      description: i18n.t(
        'Urządzenie zmieniające średnie napięcie na niskie dla domów, na słupie lub w małym budynku.'
      ),
    },
  ];

  return (
    <Modal>
      <ModalTrigger asChild>
        {variant === 'full' ? (
          <button
            type="button"
            className="md:caption-large-semi flex w-full items-center gap-2 rounded-lg border border-blue-400 bg-blue-200 px-[14px] py-[10px] font-jakarta transition-colors hover:bg-primary/5 md:px-[12px]"
          >
            <QuestionCircleIcon width={18} className="shrink-0 text-blue-600" />
            <span className="caption-large-semi text-oxford-blue-500">
              {i18n.t('Rodzaje linii przesyłowych')}
            </span>
            <RightArrowIcon
              width={16}
              className="ml-auto shrink-0 text-oxford-blue-400"
            />
          </button>
        ) : (
          <Button type="button" intent="secondary" size="small">
            {i18n.t('Rodzaje linii przesyłowych')}
          </Button>
        )}
      </ModalTrigger>
      <ModalContent className="max-w-[620px] gap-0 rounded-[28px] p-0 shadow-[0px_6px_16px_0px_rgba(0,0,0,0.16)] md:rounded-[20px] lg:p-0">
        <div className="flex w-full items-center justify-between border-b border-dark-gray-100 px-6 py-5 lg:p-6">
          <div className="flex items-center gap-2">
            <BoltIcon className="size-5 shrink-0 text-primary lg:size-6" />
            <ModalTitle className="h3 text-primary">
              {i18n.t('Rodzaje linii przesyłowych')}
            </ModalTitle>
          </div>
          <ModalClose
            aria-label={i18n.t('Zamknij')}
            className="shrink-0 rounded transition-opacity hover:opacity-70 focus:outline-none focus:ring-4 focus:ring-primary/20"
          >
            <CloseIcon className="size-5 text-dark-gray-300" />
          </ModalClose>
        </div>
        <ModalDescription className="sr-only">
          {i18n.t('Lista rodzajów linii przesyłowych wraz z opisem.')}
        </ModalDescription>
        <div className="flex w-full flex-col gap-2 p-6 lg:px-6 lg:py-5">
          {lineTypes.map((lineType) => (
            <TransmissionLineTypeItem key={lineType.title} {...lineType} />
          ))}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default TransmissionLineTypesModal;
